<template>
  <el-dialog
    title="发票管理"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    @close="handleClose"
  >
    <div v-loading="loading">
      <el-form :model="formData" label-width="120px">
        <el-form-item label="发票状态：">
          <el-radio-group v-model="formData.invoiceType" @change="handleInvoiceTypeChange">
            <el-radio :label="1">已收到纸质发票</el-radio>
            <el-radio :label="2">上传电子发票</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item v-if="formData.invoiceType === 2" label="上传发票：">
          <el-upload
            ref="upload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :file-list="fileList"
            :on-success="handleUploadSuccess"
            :on-remove="handleRemove"
            :before-upload="beforeUpload"
            multiple
            name="fileVal"
            :limit="10"
            accept=".pdf,.jpg,.png,.jpeg,.docx,.doc"
          >
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">
              支持 PDF、JPG、PNG、JPEG、DOCX、DOC 格式，单个文件不超过10MB
            </div>
          </el-upload>
        </el-form-item>
        
        <!-- 显示已有发票文件 -->
        <el-form-item v-if="existingInvoices.length > 0" label="已有发票：">
          <div class="existing-invoices">
            <div v-for="(invoice, index) in existingInvoices" :key="index" class="invoice-item">
              <el-link 
                :href="invoice" 
                target="_blank" 
                type="primary"
                :underline="false"
              >
                <i class="el-icon-document"></i>
                {{ getFileName(invoice) }}
              </el-link>
              <el-button 
                type="text" 
                size="mini" 
                style="color: #f56c6c; margin-left: 10px;"
                @click="removeExistingInvoice(index)"
              >
                删除
              </el-button>
            </div>
          </div>
        </el-form-item>
      </el-form>
    </div>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="handleSave" :loading="saving">保 存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { ygbOrderInfo, ygbOrderInvoiceUpdate } from '@/api/member.js'

export default {
  name: 'InvoiceReceived',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      saving: false,
      formData: {
        invoiceType: 1, // 1: 已收到纸质发票, 2: 上传电子发票
      },
      fileList: [], // 新上传的文件列表
      uploadedFiles: [], // 已上传的文件URL列表
      existingInvoices: [], // 已有的发票文件
      uploadUrl: process.env.VUE_APP_FILE_UPLOAD_URL || process.env.VUE_APP_BASE_URL + '/res/file/upload',
      uploadHeaders: {
        'Authorization': 'Bearer ' + this.$store.getters.token
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadOrderInfo()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 加载订单信息，获取已有的发票文件
    async loadOrderInfo() {
      if (!this.rowData.id) return
      
      this.loading = true
      try {
        const response = await ygbOrderInfo({ id: this.rowData.id })
        if (response.err_code === 0) {
          const orderInfo = response.order
          
          // 处理已有的发票文件
          if (orderInfo.invoice_url && Array.isArray(orderInfo.invoice_url)) {
            this.existingInvoices = [...orderInfo.invoice_url]
          } else if (orderInfo.invoice_url && typeof orderInfo.invoice_url === 'string') {
            try {
              this.existingInvoices = JSON.parse(orderInfo.invoice_url)
            } catch (e) {
              this.existingInvoices = orderInfo.invoice_url.split(',').filter(url => url.trim())
            }
          } else {
            this.existingInvoices = []
          }
          
          // 设置发票类型
          if (this.existingInvoices.length > 0) {
            this.formData.invoiceType = 2 // 有电子发票
          } else {
            this.formData.invoiceType = orderInfo.is_invoice_received ? 1 : 2
          }
        }
      } catch (error) {
        console.error('获取订单信息失败:', error)
        this.$message.error('获取订单信息失败')
      } finally {
        this.loading = false
      }
    },
    
    // 发票类型改变
    handleInvoiceTypeChange(value) {
      if (value === 1) {
        // 选择纸质发票时，清空上传的文件
        this.fileList = []
        this.uploadedFiles = []
      }
    },
    
    // 文件上传前检查
    beforeUpload(file) {
      const isValidType = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword'].includes(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10
      
      if (!isValidType) {
        this.$message.error('只能上传 PDF、JPG、PNG、JPEG、DOCX、DOC 格式的文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },
    
    // 文件上传成功
    handleUploadSuccess(response, file, fileList) {
      if (response.err_code === 0) {
        this.uploadedFiles.push(response.data)
        this.$message.success('文件上传成功')
      } else {
        this.$message.error('文件上传失败: ' + response.err_msg)
        // 移除上传失败的文件
        const index = this.fileList.findIndex(item => item.uid === file.uid)
        if (index > -1) {
          this.fileList.splice(index, 1)
        }
      }
    },
    
    // 移除上传的文件
    handleRemove(file, fileList) {
      // 从uploadedFiles中移除对应的URL
      if (file.response && file.response.data) {
        const index = this.uploadedFiles.indexOf(file.response.data)
        if (index > -1) {
          this.uploadedFiles.splice(index, 1)
        }
      }
    },
    
    // 移除已有的发票文件
    removeExistingInvoice(index) {
      this.existingInvoices.splice(index, 1)
    },
    
    // 获取文件名
    getFileName(url) {
      if (!url) return '发票文件'
      const parts = url.split('/')
      const fileName = parts[parts.length - 1]
      return fileName || '发票文件'
    },
    
    // 保存
    async handleSave() {
      this.saving = true
      try {
        // 合并所有发票文件URL
        const allInvoiceUrls = [...this.existingInvoices, ...this.uploadedFiles]
        
        const data = {
          order_id: this.rowData.id,
          invoice_url: allInvoiceUrls,
          is_invoice_received: true
        }
        
        // 调用发票更新API
        const response = await ygbOrderInvoiceUpdate(data)
        
        if (response.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error('保存失败: ' + response.data.err_msg)
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    
    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
      this.resetForm()
    },
    
    // 重置表单
    resetForm() {
      this.formData = {
        invoiceType: 1
      }
      this.fileList = []
      this.uploadedFiles = []
      this.existingInvoices = []
      this.loading = false
      this.saving = false
    }
  }
}
</script>

<style scoped>
.existing-invoices {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  background-color: #fafafa;
}

.invoice-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #ebeef5;
}

.invoice-item:last-child {
  border-bottom: none;
}

.invoice-item .el-link {
  flex: 1;
}
</style>